/**
 * useEnsureContractClaimed
 *
 * Ensures contracts created by Stripe webhook (agent_id = NULL) are linked
 * to the authenticated agent. Uses four strategies:
 *
 * 1. RPC claim: call `claim_agent_contracts()` (SECURITY DEFINER, bypasses RLS)
 * 2. RPC recovery: call `recover_contracts_from_proposals()` for paid proposals
 *    that never got a contract created (webhook failure recovery)
 * 3. Bridge: query contract_proposals to find contract_id, then claim directly
 * 4. Direct: match by agent_email (works only if emails match)
 *
 * After claiming, triggers a contract refresh so the UI updates.
 * Uses refs to prevent infinite re-render loops.
 *
 * RETRY LOGIC: If no contracts are found after all strategies, retries up to
 * MAX_RETRIES times with RETRY_DELAY_MS between attempts. This handles the
 * race condition where the Stripe webhook hasn't created the contract yet
 * when the user completes onboarding.
 */

import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useAgent } from "@/contexts/AgentContext";
import { logActivity } from "@/lib/activityLogger";

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 5000; // 5 seconds between retries

interface ClaimResult {
  /** Whether the claim check has completed */
  ready: boolean;
  /** IDs of active contracts now linked to this agent */
  activeContractIds: string[];
}

export function useEnsureContractClaimed(
  onClaimComplete?: () => void
): ClaimResult {
  const { user } = useAuth();
  const { state } = useAgent();
  const [result, setResult] = useState<ClaimResult>({ ready: false, activeContractIds: [] });

  // Use refs to break dependency cycles and prevent infinite loops
  const onClaimCompleteRef = useRef(onClaimComplete);
  onClaimCompleteRef.current = onClaimComplete;

  const hasRunForUser = useRef<string | null>(null);

  useEffect(() => {
    if (!user || !state.loaded) return;

    const agentId = state.profile.id;
    const agentEmail = state.profile.email || user.email || "";

    // Only run once per user session
    if (!agentId || !agentEmail || hasRunForUser.current === agentId) return;
    hasRunForUser.current = agentId;

    const attemptClaim = async (attempt: number): Promise<string[]> => {
      console.log(`[useEnsureContractClaimed] Attempt ${attempt + 1}/${MAX_RETRIES + 1} for ${agentId} (${agentEmail})`);

      // Step 1: Check if we already have contracts linked by agent_id
      const { data: linked } = await supabase
        .from("contracts")
        .select("id, status")
        .eq("agent_id", agentId);

      if (linked && linked.length > 0) {
        const active = linked.filter((c) => c.status === "active").map((c) => c.id);
        console.log("[useEnsureContractClaimed] Already linked:", active.length, "active contracts");
        return active;
      }

      // Step 2: Try RPC claim function (SECURITY DEFINER, bypasses RLS)
      console.log("[useEnsureContractClaimed] No linked contracts, trying RPC claim...");
      try {
        const { data: rpcResult, error: rpcError } = await (supabase.rpc as any)(
          "claim_agent_contracts"
        );
        if (!rpcError && rpcResult) {
          console.log("[useEnsureContractClaimed] RPC claim result:", rpcResult);
          if (rpcResult.claimed_direct > 0 || rpcResult.claimed_via_proposal > 0) {
            logActivity({
              event_type: "contract_claimed",
              actor_id: agentId,
              actor_email: agentEmail,
              entity_type: "contract",
              entity_id: agentId,
              metadata: rpcResult,
            });
          }
        } else if (rpcError) {
          console.log("[useEnsureContractClaimed] RPC claim not available:", rpcError.message);
        }
      } catch {
        console.log("[useEnsureContractClaimed] RPC claim call failed");
      }

      // Step 2b: Try contract recovery — creates contracts from proposals
      // that were paid but never got a contract (Stripe webhook failure recovery).
      console.log("[useEnsureContractClaimed] Trying proposal recovery...");
      try {
        const { data: recoveryResult, error: recoveryError } = await (supabase.rpc as any)(
          "recover_contracts_from_proposals"
        );
        if (!recoveryError && recoveryResult) {
          console.log("[useEnsureContractClaimed] Recovery result:", recoveryResult);
          if (recoveryResult.recovered > 0) {
            console.log(`[useEnsureContractClaimed] Recovered ${recoveryResult.recovered} contracts from proposals`);
            logActivity({
              event_type: "contract_recovered",
              actor_id: agentId,
              actor_email: agentEmail,
              entity_type: "contract",
              entity_id: agentId,
              metadata: { recovered: recoveryResult.recovered, linked: recoveryResult.linked },
            });
          }
        } else if (recoveryError) {
          console.log("[useEnsureContractClaimed] Recovery RPC not available:", recoveryError.message);
        }
      } catch {
        console.log("[useEnsureContractClaimed] Recovery call failed");
      }

      // Step 3: Fallback — try to claim via contract_proposals bridge
      // Only fetch proposals belonging to THIS agent (by auth_user_id or email)
      console.log("[useEnsureContractClaimed] Trying contract_proposals bridge...");
      try {
        const { data: proposals } = await supabase
          .from("contract_proposals" as any)
          .select("contract_id, agent_email")
          .not("contract_id", "is", null)
          .or(`auth_user_id.eq.${user.id},agent_email.eq.${agentEmail}`) as any;

        if (proposals && proposals.length > 0) {
          console.log("[useEnsureContractClaimed] Found proposals with contract_ids:", proposals.length);
          for (const proposal of proposals) {
            if (proposal.contract_id) {
              const { error: claimErr } = await supabase
                .from("contracts")
                .update({ agent_id: agentId, agent_email: agentEmail } as any)
                .eq("id", proposal.contract_id)
                .is("agent_id", null);

              if (claimErr) {
                console.log("[useEnsureContractClaimed] Claim via proposal failed:", claimErr.message);
              } else {
                console.log("[useEnsureContractClaimed] Claimed contract via proposal:", proposal.contract_id);
              }
            }
          }
        }
      } catch (err) {
        console.warn("[useEnsureContractClaimed] Proposal bridge failed:", err);
      }

      // Step 4: Direct email match — claim unclaimed contracts
      console.log("[useEnsureContractClaimed] Trying direct email claim for:", agentEmail);
      await supabase
        .from("contracts")
        .update({ agent_id: agentId } as any)
        .eq("agent_email", agentEmail)
        .is("agent_id", null);

      // Step 5: Re-query to see what THIS agent owns now
      const { data: afterClaim } = await supabase
        .from("contracts")
        .select("id, status, agent_id, agent_email")
        .or(`agent_id.eq.${agentId},agent_email.eq.${agentEmail}`);

      console.log("[useEnsureContractClaimed] Agent contracts after claim:", afterClaim);
      return (afterClaim || []).filter((c) => c.status === "active").map((c) => c.id);
    };

    const run = async () => {
      let activeIds = await attemptClaim(0);

      // Retry if no contracts found — handles race condition where the
      // Stripe webhook hasn't created the contract yet
      if (activeIds.length === 0) {
        // Check if this agent has a paid proposal (indicates a contract SHOULD exist)
        const { data: paidProposals } = await supabase
          .from("contract_proposals" as any)
          .select("id, status")
          .or(`auth_user_id.eq.${user.id},agent_email.eq.${state.profile.email || user.email}`)
          .in("status", ["contract_created", "paid"]) as any;

        const hasPaidProposal = paidProposals && paidProposals.length > 0;

        if (hasPaidProposal) {
          console.log(`[useEnsureContractClaimed] No contracts found but agent has paid proposal(s). Retrying up to ${MAX_RETRIES} times...`);

          for (let retry = 1; retry <= MAX_RETRIES; retry++) {
            await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
            activeIds = await attemptClaim(retry);
            if (activeIds.length > 0) {
              console.log(`[useEnsureContractClaimed] Contract found on retry ${retry}`);
              break;
            }
          }

          if (activeIds.length === 0) {
            console.warn("[useEnsureContractClaimed] Contract not found after all retries. Stripe webhook may be delayed.");
          }
        }
      }

      console.log("[useEnsureContractClaimed] Active contract IDs:", activeIds);
      setResult({ ready: true, activeContractIds: activeIds });

      // Notify caller so they can refresh contracts context
      if (onClaimCompleteRef.current) {
        onClaimCompleteRef.current();
      }
    };

    run();
  }, [user, state.loaded, state.profile.id, state.profile.email]);

  return result;
}
